import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FullTable, SelectAutocompletePaginationMultiple, TabSwitcher } from 'components';
import { adminActions } from 'store';
import { FindLoad, renderStaffName, staffStatusEnums } from 'utils';
import moment from 'moment';
import { statusFilterEnums, statusFilterTypes, summaryHead, summaryBody } from './constants';
import './staffSummaryModal.scss';

const ACTION_TYPE = 'GET_STAFF_SUPERVISION_SUMMARY';

export const StaffSummaryModal = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.admins.staffSupervisionSummary);
    const [selectedMonth, setSelectedMonth] = useState(moment().format('YYYY-MM'));
    const [selectedStaffs, setSelectedStaffs] = useState([]);
    const [statusFilter, setStatusFilter] = useState(statusFilterEnums.ALL);
    const [sortParams, setSortParams] = useState({});

    useEffect(() => {
        dispatch(adminActions.getStaffSupervisionSummary({ month: selectedMonth }));
    }, [selectedMonth]);

    const filteredItems = useMemo(() => {
        const items = data?.items || [];
        let filtered = [...items];

        // Filter by selected staffs
        if (selectedStaffs?.length) {
            const selectedIds = selectedStaffs.map((s) => s?.id);
            filtered = filtered.filter((item) => selectedIds.includes(item?.staff?.id));
        }

        // Filter by compliance status
        if (statusFilter === statusFilterEnums.COMPLIANT) {
            filtered = filtered.filter((item) => item?.isCompliant);
        } else if (statusFilter === statusFilterEnums.NON_COMPLIANT) {
            filtered = filtered.filter((item) => !item?.isCompliant);
        }


        // Sort
        const { sortType, orderType } = sortParams;
        if (sortType) {
            const dir = orderType === 'DESC' ? -1 : 1;
            filtered.sort((a, b) => {
                switch (sortType) {
                    case 'fullName':
                        return dir * (a?.staff?.lastName || '').localeCompare(b?.staff?.lastName || '');
                    case 'serviceHours':
                        return dir * ((a?.serviceHours || 0) - (b?.serviceHours || 0));
                    case 'supervisionHoursReceived':
                        return dir * ((a?.supervisionHoursReceived || 0) - (b?.supervisionHoursReceived || 0));
                    case 'supervisionPercent':
                        return dir * ((a?.supervisionPercent || 0) - (b?.supervisionPercent || 0));
                    case 'contactsCount':
                        return dir * ((a?.contactsCount || 0) - (b?.contactsCount || 0));
                    case 'isCompliant':
                        return dir * ((a?.isCompliant ? 1 : 0) - (b?.isCompliant ? 1 : 0));
                    default:
                        return 0;
                }
            });
        } else {
            // Default: non-compliant first, then by name
            filtered.sort((a, b) => {
                if (a?.isCompliant !== b?.isCompliant) return a?.isCompliant ? 1 : -1;
                return (a?.staff?.lastName || '').localeCompare(b?.staff?.lastName || '');
            });
        }

        return filtered;
    }, [data, selectedStaffs, statusFilter, sortParams]);

    const handleStaffSelect = (e) => {
        if (e?.target?.value === 'All') {
            setSelectedStaffs([]);
        } else {
            setSelectedStaffs(e?.target?.full || []);
        }
    };

    const compliantCount = data?.compliant || 0;
    const nonCompliantCount = data?.nonCompliant || 0;

    return (
        <div className="staff-summary-modal-wrapper">
            <div className="staff-summary-header-row">
                <div className="staff-summary-title-row">
                    <p className="modal-header-title">Staff Supervision Summary</p>
                    <div className="staff-summary-total-badge" style={{ borderColor: '#0C66E3' }}>
                        <span className="staff-summary-total-count" style={{ color: '#175CD3' }}>
                            {filteredItems?.length || 0}
                        </span>
                        <span className="staff-summary-total-label">Total Staff</span>
                    </div>
                    {compliantCount > 0 && (
                        <div className="staff-summary-total-badge" style={{ borderColor: '#038A4D' }}>
                            <span className="staff-summary-total-count" style={{ color: '#027A48' }}>
                                {compliantCount}
                            </span>
                            <span className="staff-summary-total-label">Compliant</span>
                        </div>
                    )}
                    {nonCompliantCount > 0 && (
                        <div className="staff-summary-total-badge" style={{ borderColor: '#F04438' }}>
                            <span className="staff-summary-total-count" style={{ color: '#B42318' }}>
                                {nonCompliantCount}
                            </span>
                            <span className="staff-summary-total-label">Non-Compliant</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="staff-summary-filters-row space-between">
                <TabSwitcher list={statusFilterTypes} defaultType={statusFilterEnums.ALL} tab={statusFilter} setTab={setStatusFilter} />


                <div className='flex-align-end' style={{gap: 12}}>
               <div className="staff-summary-staff-filter">
                    <SelectAutocompletePaginationMultiple
                        defaultStatuses={[staffStatusEnums.ACTIVE]}
                        outLabel={'Staff'}
                        uiType={'tableFilter'}
                        placeholder={selectedStaffs?.length ? selectedStaffs?.map((i) => renderStaffName(i)).join(', ') : 'All'}
                        name={'staffs'}
                        handleSelect={handleStaffSelect}
                        defaultValue={selectedStaffs}
                        renderValue={(i) => renderStaffName(i)}
                        type={'staff'}
                        noError={true}
                        autoSelect={true}
                    />
                </div>
                <div className="staff-summary-month-picker">
                    <input
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                    />
                </div>
                </div>
            </div>

            <FullTable
                head={summaryHead}
                body={summaryBody}
                loadingType={ACTION_TYPE}
                list={filteredItems}
                noText={'supervision data'}
                activeRowId=""
                height={'medium'}
                getParams={setSortParams}
                savedParams={sortParams}
                notYet={{
                    title: 'No supervision data for this month',
                }}
            />
        </div>
    );
};
